import React, { useMemo, useState, useEffect } from 'react';

// MUI
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';

// hooks
import { useExtractionDetail } from '../../../hooks/useExtraction';
import EllipsisTooltip from '../../../components/tooltipText';

/* ================= Helper ================= */

const renderCellValue = (value) => {
  if (value === null || value === undefined) return '-';

  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '-';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

const formatHeader = (key) =>
  key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toUpperCase();

const parseValue = (value) => {
  if (value === '') return null;
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (!isNaN(value)) return Number(value);
  return value;
};

/* ================= Component ================= */

export default function EditDataPage({ open, onClose, onConfirm, dataView }) {
  const table = dataView?.table_name;

  const { data: dataExtractionDetail, isFetching, isError } = useExtractionDetail({ table });

  const rows = dataExtractionDetail?.data ?? [];

  const [editableRows, setEditableRows] = useState([]);

  // modal edit
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (rows.length) {
      setEditableRows(rows);
    }
  }, [rows]);

  const columns = useMemo(() => {
    if (!rows.length) return [];
    return Object.keys(rows[0]);
  }, [rows]);

  /* ================= Handlers ================= */

  const handleOpenEdit = (row) => {
    setSelectedRow(row);
    setFormData(row);
    setOpenEditModal(true);
  };

  const handleCloseEdit = () => {
    setOpenEditModal(false);
    setSelectedRow(null);
  };

  const handleFormChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveEdit = () => {
    const updatedRows = editableRows.map((row) => {
      if (row.id === selectedRow.id) {
        return { ...formData };
      }
      return row;
    });

    setEditableRows(updatedRows);
    handleCloseEdit();
  };

  const handleSave = () => {
    const finalPayload = editableRows.map((row) => {
      const newRow = {};
      Object.keys(row).forEach((key) => {
        newRow[key] = parseValue(row[key]);
      });
      return newRow;
    });

    console.log('FINAL PAYLOAD:', finalPayload);
    onConfirm(finalPayload);
  };

  /* ================= Header Title ================= */

  const title = [
    dataView?.metadata?.connection_user_name,
    dataView?.metadata?.hub_name,
    dataView?.metadata?.project_name || dataView?.metadata?.name
  ]
    .filter(Boolean)
    .join(' | ');

  /* ================= Render ================= */

  return (
    <>
      {/* MAIN DIALOG */}
      <Dialog fullScreen open={open} onClose={onClose}>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1, fontWeight: 'bold' }} variant="h6">
              {title}
            </Typography>

            <Button color="inherit" onClick={onClose}>
              CLOSE
            </Button>
            <Button color="inherit" onClick={handleSave}>
              SAVE
            </Button>

            
          </Toolbar>
        </AppBar>

        <Box p={2}>
          {isFetching && (
            <Box mt={5} display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          )}

          {isError && (
            <Typography align="center" color="error">
              Failed to load data
            </Typography>
          )}

          {!isFetching && !rows.length && <Typography>No data available</Typography>}

          {!!rows.length && (
            <TableContainer>
              <Table size="small">
                {/* HEAD */}
                <TableHead>
                  <TableRow>
                    <TableCell>EDIT</TableCell>
                    {columns.map((col) => (
                      <TableCell key={col}>
                        <strong>{formatHeader(col)}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                {/* BODY */}
                <TableBody>
                  {editableRows.map((row, rowIndex) => (
                    <TableRow key={row.id ?? rowIndex}>
                      {/* EDIT BUTTON */}
                      <TableCell>
                        <IconButton onClick={() => handleOpenEdit(row)}>
                          <EditIcon sx={{ color: 'green' }} fontSize="small" />
                        </IconButton>
                      </TableCell>

                      {columns.map((col) => (
                        <TableCell
                          key={col}
                          sx={{
                            maxWidth: 240,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          <EllipsisTooltip>{renderCellValue(row[col])}</EllipsisTooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog open={openEditModal} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        {/* <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <Typography sx={{ flex: 1 }} variant="h6">
              Edit Row
            </Typography>

            <Button color="inherit" onClick={handleSaveEdit}>
              SAVE
            </Button>

            <Button color="inherit" onClick={handleCloseEdit}>
              CLOSE
            </Button>
          </Toolbar>
        </AppBar> */}
        <DialogTitle id="alert-dialog-title">Edit Row Data</DialogTitle>
        <DialogContent>
          <Box p={3}>
            {columns.map((col) => (
              <Box key={col} mb={2}>
                <Typography variant="caption">{formatHeader(col)}</Typography>

                <TextField fullWidth value={formData[col] ?? ''} onChange={(e) => handleFormChange(col, e.target.value)} />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>CANCEL</Button>
          <Button variant='contained' onClick={handleSaveEdit} autoFocus>
            SAVE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
